import { Box, Divider, Grid, SxProps, Theme, Typography } from '@mui/material'
import SubPane, { PaneOptions } from './SubPane'
import { UserData } from '../../UserPage/useUserPage'
import { NotesLimitLevel } from '@types'
import { DateTime } from 'luxon'
import { formatDurationHoursMinutes } from '@utils'

type Settings = NonNullable<UserData['settings']>

interface Props {
    setting:
        | Required<Settings['orgSetting']>
        | Required<Settings['groupSetting']>
        | Required<Settings['userSetting']>
    limit:
        | Required<Settings['orgDefLimit']>
        | Required<Settings['groupLimit']>
        | Required<Settings['userLimit']>
    /**
     * Seconds, which is what habitat-admin returns. Named for the unit
     * because the bar next to it is in hours: Staff Console used to divide
     * this down before it left the server, and when the resolver became the
     * source it started arriving raw, so the bar read "3600/1 Hours".
     */
    usageSeconds: number
    /** Which level supplied the rule, for the heading. */
    settingLevel?: string | null
    /** Which level supplied the cap. Resolved separately, often different. */
    limitLevel?: NotesLimitLevel | null
    sx?: SxProps<Theme>
}

/**
 * Which level a pane is describing, for its heading.
 *
 * This used to be a two way choice: organisation, or "individual" for
 * everything else. Group was not a case, so a rule inherited from a group
 * read as the student's own, directly contradicting the line above the pane
 * saying it came from their group. QA found it on a grouped student whose
 * access was correct but described as individual.
 *
 * The setting carries `setting_type` ('allowed' | 'blocked' | 'org') and the
 * limit carries `type` ('user' | 'group' | 'org'), which is why they are read
 * differently here. Neither says "group" on its own, so the level has to be
 * passed in.
 */
const LEVEL_NAMES: Record<NotesLimitLevel | 'user' | 'group' | 'org', string> =
    {
        user: 'individual',
        group: 'group',
        org: 'organisation',
        fallback: 'default',
    }

function paneLevel(level: string | null | undefined): string {
    if (!level) return 'organisation'
    return LEVEL_NAMES[level as keyof typeof LEVEL_NAMES] ?? level
}

export default function SettingLimitPane({
    setting,
    limit,
    usageSeconds,
    settingLevel,
    limitLevel,
    sx,
}: Props) {
    let settingPaneOpts
    let limitPaneOpts
    if (setting) {
        settingPaneOpts = {
            status: {
                value:
                    setting.setting_type === 'org' ||
                    setting.setting_type === 'allowed'
                        ? 'Active'
                        : 'Inactive',
                display: 'text',
                help:
                    'Whether this rule permits ordering. Active does not mean ' +
                    'they can order right now: the allowance is a separate ' +
                    'check, shown alongside. Inactive means this rule itself ' +
                    'blocks ordering.',
            },
            turnaround_time: {
                value: `${setting.service.turnaround_time} Hours`,
                display: 'text',
            },
            currency: {
                value: setting.service.currency,
                display: 'text',
            },
            from_date: {
                value: (typeof setting.setting.from_date === 'string'
                    ? DateTime.fromISO(setting.setting.from_date)
                    : DateTime.fromJSDate(setting.setting.from_date)
                ).toFormat('DD'),
                custom_name: 'Start Date',
                display: 'text',
            },
            to_date: {
                value: setting.setting.to_date
                    ? (typeof setting.setting.to_date === 'string'
                          ? DateTime.fromISO(setting.setting.to_date)
                          : DateTime.fromJSDate(setting.setting.to_date)
                      ).toFormat('DD')
                    : 'N/A',
                display: 'text',
                custom_name: 'End Date',
            },
        } as PaneOptions<
            typeof setting.setting & typeof setting.service & { status: string }
        >
    } else {
        settingPaneOpts = {
            status: {
                value: 'Inactive',
                display: 'text',
            },
            turnaround_time: {
                value: 'N/A',
                display: 'text',
            },
            currency: {
                value: 'N/A',
                display: 'text',
            },
            from_date: {
                value: 'N/A',
                custom_name: 'Start Date',
                display: 'text',
            },
            to_date: {
                value: 'N/A',
                display: 'text',
                custom_name: 'End Date',
            },
        } as PaneOptions<{
            status: string
            turnaround_time: string
            currency: string
            from_date: string
            to_date: string
        }>
    }

    if (limit) {
        limitPaneOpts = {
            limit: {
                display: 'progress',
                // Seconds both sides. The bar only uses the ratio, and the
                // line above it is written out rather than divided down.
                value: usageSeconds,
                total: limit.limit.limit,
                text: `${formatDurationHoursMinutes(
                    usageSeconds
                )} of ${formatDurationHoursMinutes(limit.limit.limit)}`,
                custom_name: 'Usage',
                help:
                    'Counts orders finished inside this period. An order that ' +
                    'is placed, assigned or still being worked on is not ' +
                    'included, which is how a total can end up past the cap.',
            },
            period: {
                value:
                    limit.limit.period === 'full-month'
                        ? 'Every month'
                        : 'Every half month',
                custom_name: 'Refresh',
                display: 'text',
            },
            from_date: {
                value: (typeof limit.limit.from_date === 'string'
                    ? DateTime.fromISO(limit.limit.from_date)
                    : DateTime.fromJSDate(limit.limit.from_date)
                ).toFormat('DD'),
                custom_name: 'Start Date',
                display: 'text',
            },
            to_date: {
                value: limit.limit.to_date
                    ? typeof limit.limit.to_date === 'string'
                        ? DateTime.fromISO(limit.limit.to_date).toFormat('DD')
                        : DateTime.fromJSDate(limit.limit.to_date)
                    : 'N/A',
                display: 'text',
                custom_name: 'End Date',
            },
        } as PaneOptions<
            typeof limit.limit & typeof limit.service & { status: string }
        >
    }

    return (
        <Box sx={{ width: '100%', ...sx }}>
            <Typography variant="h5" sx={{ textAlign: 'left' }}>
                Summary Notes
            </Typography>
            <Divider sx={{ my: '0.1em' }} />
            <Box sx={{ width: '100%', my: '0.5em' }}>
                <Grid container spacing={2}>
                    {settingPaneOpts && (
                        <Grid item xs={6} sx={{ height: '100%' }}>
                            <SubPane
                                opts={settingPaneOpts}
                                title={`Setting (${paneLevel(settingLevel)})`}
                            />
                        </Grid>
                    )}
                    {limitPaneOpts && (
                        <Grid item xs={6} height={'100%'}>
                            <SubPane
                                opts={limitPaneOpts}
                                title={`Limit (${paneLevel(limitLevel)})`}
                            />
                        </Grid>
                    )}
                </Grid>
            </Box>
        </Box>
    )
}
